import { Cartesian3, Math as CesiumMath, type Viewer } from 'cesium';

import type { AssetDetailType, AssetHomeView } from '@/common/asset';

const round = (value: number, decimals: number) => Number(value.toFixed(decimals));

/** The camera's current pose as a saveable home view. */
export function captureHomeView(viewer: Viewer): AssetHomeView {
	const { camera } = viewer;
	const position = camera.positionCartographic;
	return {
		lon: round(CesiumMath.toDegrees(position.longitude), 7),
		lat: round(CesiumMath.toDegrees(position.latitude), 7),
		height: round(position.height, 2),
		heading: round(CesiumMath.toDegrees(camera.heading), 3),
		pitch: round(CesiumMath.toDegrees(camera.pitch), 3),
		roll: round(CesiumMath.toDegrees(camera.roll), 3),
	};
}

/** `camera.flyTo` options that restore a saved home view. */
export function homeViewToFlyOptions(view: AssetHomeView) {
	return {
		destination: Cartesian3.fromDegrees(view.lon, view.lat, view.height),
		orientation: {
			heading: CesiumMath.toRadians(view.heading),
			pitch: CesiumMath.toRadians(view.pitch),
			roll: CesiumMath.toRadians(view.roll),
		},
	};
}

type AssetPosition = Pick<AssetDetailType, 'homeView' | 'latitude' | 'longitude' | 'height'>;

/**
 * `camera.flyTo` options for where the asset is: its saved home view, else straight down onto its
 * coordinates. Null when it has neither.
 */
export function assetPositionFlyOptions(asset: AssetPosition) {
	if (asset.homeView) {
		return homeViewToFlyOptions(asset.homeView);
	}
	const { latitude, longitude } = asset;
	// 0 is the "not set" default of older assets, not a place
	if (!latitude || !longitude || Number.isNaN(latitude) || Number.isNaN(longitude)) {
		return null;
	}
	return {
		destination: Cartesian3.fromDegrees(longitude, latitude, asset.height || 500),
		orientation: { heading: 0.0, pitch: CesiumMath.toRadians(-90.0), roll: 0.0 },
	};
}
